<template>
  <div>
    <comment
      v-for="comment in visibleComments"
      :key="comment.id"
      :comment="comment"
      @deleteComment="deleteComment"
      @editComment="editComment"
    />
    <div v-if="addCommentVisible" class="mb-2">
      <h6>{{ $t('comments.new') }}:</h6>
      <rich-text-comments v-model="newComment" />
      <b-button
        size="sm"
        variant="primary"
        class="mr-2"
        @click="addComment"
        :disabled="disableAddComment"
      >
        <b-spinner v-if="saving" small></b-spinner>
        {{ $t('comments.add') }}
      </b-button>
      <b-button size="sm" variant="secondary" @click="resetAddComment">
        {{ $t('comments.cancel') }}
      </b-button>
    </div>
    <b-button
      v-else-if="!disableNewButton"
      size="sm"
      variant="primary"
      :disabled="disableNewComments"
      @click="addCommentVisible = true"
    >
      {{ $t('comments.new') }}
    </b-button>
  </div>
</template>

<script>
import Comment from './Comment';
import axios from 'axios';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import RichTextComments from './inputs/RichTextComments.vue';
import { CommentActionTypeEnum } from '@gid/models/dist/entities/comment';

export default {
  components: {
    Comment,
    RichTextComments,
  },
  props: {
    jobApiId: String,
    disableListComments: Boolean,
    disableNewButton: Boolean,
    disableNewComments: Boolean,
  },
  data() {
    return {
      endpoint: '/data-api/comment',
      comments: [],
      newComment: {
        message: null,
        visible_for_roles: [],
        action_type: CommentActionTypeEnum.GENERIC,
        took_action: false,
      },
      addCommentVisible: false,
      saving: false,
    };
  },
  computed: {
    visibleComments() {
      return this.comments;
    },
    commentToSave() {
      return {
        message: this.newComment.message,
        visibleForRoles: this.newComment.visible_for_roles,
        jobApiId: this.jobApiId,
        actionType: this.newComment.action_type,
      };
    },
    disableAddComment() {
      return (
        this.saving ||
        !this.newComment.message ||
        (this.newComment.took_action &&
          this.newComment.action_type == CommentActionTypeEnum.GENERIC)
      );
    },
  },
  async created() {
    if (!this.disableListComments) {
      this.loadComments();
    }
  },
  methods: {
    async loadComments() {
      const queryString = RequestQueryBuilder.create({
        filter: {
          field: `jobApiId`,
          operator: CondOperator.EQUALS,
          value: this.jobApiId,
        },
      }).query();

      this.comments = await axios
        .get(`${this.endpoint}?${queryString}`)
        .then(({ data }) => data);
    },
    async addComment() {
      this.saving = true;
      try {
        const { data } = await axios.post(this.endpoint, this.commentToSave);
        this.$emit('comment-add', data);
        this.resetAddComment();
        if (data.actionType != CommentActionTypeEnum.GENERIC) {
          this.$emit('comment-action');
        }
        if (!this.disableListComments) {
          this.comments.push(data);
        }
      } catch (error) {
        this.$bvToast.toast(this.$t('comments.error.add.message'), {
          title: this.$t('comments.error.add.title'),
          variant: 'danger',
        });
      }
      this.saving = false;
    },
    async deleteComment(comment) {
      if (confirm('Are you sure you want to delete this comment?')) {
        await axios.delete(`${this.endpoint}/${comment.id}`);
        if (comment.actionType != CommentActionTypeEnum.GENERIC) {
          this.$emit('comment-action');
        }
        if (!this.disableListComments) {
          this.comments = this.comments.filter(({ id }) => comment.id != id);
        }
        this.$emit('comment-delete', comment);
      }
    },
    async editComment(comment) {
      const { data: changedComment } = await axios.patch(
        `${this.endpoint}/${comment.id}`,
        comment,
      );

      this.$emit('comment-update', changedComment);
      if (!this.disableListComments) {
        const oldComment = this.comments.find(
          ({ id }) => id == changedComment.id,
        );
        if (
          oldComment.actionType == CommentActionTypeEnum.GENERIC &&
          changedComment.actionType != oldComment.actionType
        ) {
          this.$emit('comment-action');
        }
        oldComment.message = changedComment.message;
        oldComment.visibleForRoles = changedComment.visibleForRoles;
        oldComment.actionType = changedComment.actionType;
        oldComment.updatedAt = changedComment.updatedAt;
      }
    },
    showAddComment() {
      this.addCommentVisible = true;
    },
    resetAddComment() {
      this.addCommentVisible = false;
      this.newComment = {
        message: null,
        visible_for_roles: [],
        action_type: CommentActionTypeEnum.GENERIC,
      };
    },
  },
};
</script>

<style lang="scss">
.gid-comment {
  background: #fafafa;
  border: 1px solid rgba($color: #000, $alpha: 0.1);

  .material-icons {
    font-size: 1em;
    vertical-align: middle;
  }
}
</style>
